<template>
  <centered-card>
    <v-form
      ref="form"
      @submit.prevent="login"
    >
      <v-card-text>
        <v-alert
          v-if="$route.query.forgot"
          type="success"
        >
          {{ $t('passwords.sent') }}
        </v-alert>
        <v-alert
          v-if="$route.query.reset"
          type="success"
        >
          {{ $t('passwords.reset') }}
        </v-alert>
        <v-alert
          v-if="$route.query.verified === 'true'"
          type="success"
        >
          {{ $t('auth.verify_success') }}
        </v-alert>
        <v-alert
          v-else-if="$route.query.verified === 'sent'"
          type="success"
        >
          {{ $t('auth.verify_sent') }}
        </v-alert>
        <v-alert
          v-else-if="['expired', 'false'].includes($route.query.verified)"
          type="error"
          prominent
        >
          <v-row align="center">
            <v-col class="grow">
              {{ $t($route.query.verified === 'expired' ? 'auth.verify_expired' : 'auth.verify_failed') }}
            </v-col>
            <v-col class="shrink">
              <v-btn
                @click="$router.push({ name: 'email-verify' })"
              >
                {{ $t('auth.verify_resend') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
        <v-alert
          v-if="$route.query.register"
          type="success"
        >
          {{ $t('common.register_success') }}
        </v-alert>
        <v-alert
          v-if="this.errors.expired.length"
          type="error"
          prominent
          outlined
          border="left"
        >
          <v-row>
            <v-col
              cols="12"
            >
              {{ this.errors.expired[0] }}
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
            >
              <v-btn
                block
                elevation="0"
                color="error"
                :to="{ name: 'forgot-password' }"
              >
                {{ $t('auth.request_reset') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
        <v-alert
          v-if="errors.unknown.length"
          type="error"
          prominent
        >
          {{ errors.unknown[0] }}
        </v-alert>
        <v-text-field
          id="email"
          prepend-icon="person"
          name="email"
          type="email"
          :label="$t('common.email_address')"
          placeholder=" "
          persistent-placeholder
          :rules="rules.email"
          validate-on-blur
          :error-messages="errors.email"
          v-model="email"
          autocomplete="username"
          required
        />
        <v-text-field
          id="current-password"
          prepend-icon="lock"
          name="password"
          type="password"
          :label="$t('common.password')"
          placeholder=" "
          persistent-placeholder
          :rules="rules.password"
          validate-on-blur
          :error-messages="errors.password"
          v-model="password"
          autocomplete="current-password"
          required
        />
      </v-card-text>
      <v-card-actions>
        <v-btn
          type="submit"
          color="primary"
          large
          block
        >
          {{ $t('auth.login') }}
        </v-btn>
      </v-card-actions>
    </v-form>
    <v-btn
      slot="after-card"
      text
      large
      :to="{ name: 'forgot-password' }"
    >
      {{ $t('auth.forgot') }}
    </v-btn>
    <v-spacer />
    <v-btn
      slot="after-card"
      text
      large
      :to="{ name: 'register' }"
    >
      {{ $t('auth.register') }}
    </v-btn>
    <external-links slot="after-card" />
  </centered-card>
</template>

<script>
import {
  ruleRequired, ruleEmail, ruleMinLength, ruleRegex,
} from '../../../utils/rules';
import { clearGraphView } from '../../../utils/preferences';
import ExternalLinks from '../../elements/external-links.vue';

export default {
  name: 'Login',
  components: { ExternalLinks },
  props: {
    redirect: {
      type: String,
      default: '/',
    },
  },
  data() {
    return {
      email: '',
      password: '',
      errors: {
        unknown: [],
        email: [],
        password: [],
        expired: [],
      },
    };
  },
  computed: {
    rules() {
      return {
        email: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.email_address') })),
          ruleEmail(this.$t('validation.email', { attribute: this.$t('common.email_address') })),
        ],
        password: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.password') })),
          ruleMinLength(8, this.$t('validation.min.string', { attribute: this.$t('common.password'), min: 8 })),
          ruleRegex(/[A-Z]/, this.$t('validation.upper_char', { attribute: this.$t('common.password') })),
          ruleRegex(/[^A-Za-z0-9]/, this.$t('validation.special_char', { attribute: this.$t('common.password') })),
        ],
      };
    },
  },
  methods: {
    resetErrors() {
      this.errors.unknown = [];
      this.errors.email = [];
      this.errors.password = [];
      this.errors.expired = [];
    },
    async login() {
      if (!this.$refs.form.validate()) return;

      try {
        this.resetErrors();
        const { data = {} } = await this.http.post('/login', {
          email: this.email,
          password: this.password,
          remember: false, // For 21CFR P.11 Compliance, "Remember Me" was removed
        });

        if (data.user) {
          this.$store.commit('login', data.user);
          clearGraphView();
          await this.$router.push({ name: 'home' });
          return;
        }

        throw new Error('Invalid Login');
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          const { errors = {} } = e.response.data;
          this.errors.email = errors.email || [];
          this.errors.password = errors.password || [];
          this.errors.expired = errors.expired || [];
          return;
        }

        if (e && e.message !== 'Invalid Login') {
          // eslint-disable-next-line no-console
          console.error('[Login] Caught unknown exception during login', e);
        }
        this.errors.unknown = [this.$t('common.unknown_error')];
      }
    },
  },
};
</script>
